import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Layout from '../../layout/Layout';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/SearchBar';
import inventoryService, { Inventory, InventoryFilter, InventoryUpdate, SkuImageInfo } from '../../../services/inventoryService';
import { useError } from '../../common/ErrorHandler';
import withErrorHandling, { WithErrorHandlingProps } from '../../../utils/withErrorHandling';
import notesIcon from '../../../assets/notesIcon.png';
import backButtonIcon from '../../../assets/backButton.png';
import customerAddIcon from '../../../assets/customerAddIcon.png';
import supplierBulkUploadIcon from '../../../assets/supplierBulkUploadIcon.png';
import imageUploadIcon from '../../../assets/supplierBulkUploadIcon.png';
import { InventoryItem } from '../../../types/inventory';
import { useToast } from '../../../hooks/useToast';
import { BackButtonComponent } from '../../ui/DesignSystem';
import BulkUploadModal from '../BulkUploadModal';
import ImageUploadModal from '../ImageUploadModal';
import PageLoadingSpinner from '../../ui/PageLoadingSpinner';
import { Toast } from '../../common/Toast';
import LoadingSpinner from '../../ui/LoadingSpinner';
import SkuImage from '../../common/SkuImage';
import customerEditIcon from '../../../assets/customerEditIcon.png';

const PageWrapper = styled.div`
  background: white;
  margin: 0;
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  padding: 0 24px;
  overflow-x: auto;
  
  /* Enable proper horizontal scrolling on all screen sizes */
  @media (max-width: 1200px) {
    padding: 0 16px;
  }
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const ContentContainer = styled.div`
  padding: 0 0 32px 0;
  flex: 1;
  overflow-x: auto;
  overflow-y: visible;
  min-width: 0; /* Allow flex shrinking */
`;

const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 12px;
  
  /* Responsive stacking */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: #042B41;
  font-weight: 500;
  min-height: 44px; /* Touch target size */
  padding: 8px;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(4, 43, 65, 0.1);
  }
  
  img {
    width: clamp(20px, 2.5vw, 24px);
    height: clamp(20px, 2.5vw, 24px);
    margin-right: 8px;
  }
`;

const PageContainer = styled.div`
  padding: 0rem;
  overflow-x: auto;
`;

const PageTitle = styled.h1`
  color: #042B41;
  font-size: clamp(24px, 4vw, 32px);
  font-weight: 800;
  margin: 0;
  display: inline-flex;
  align-items: center;
  
  /* Progressive scaling for extreme zoom */
  @media (min-resolution: 192dpi) {
    font-size: clamp(20px, 3.5vw, 28px);
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

// Enhanced TableContainer with better scrolling
const TableContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto !important;
  overflow-y: visible;
  margin-bottom: 1rem;
  border: 1px solid #e0e0e0;
  position: relative;
  
  /* Force horizontal scrolling */
  min-width: 0;
  width: 100%;
  
  /* Enhanced scrollbar for better visibility */
  &::-webkit-scrollbar {
    height: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(90deg, #888, #666);
    border-radius: 6px;
    border: 1px solid #ccc;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(90deg, #666, #444);
  }
  
  /* Ensure scrolling works on all devices */
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: clamp(8px, 1vw, 10px);
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 10px;
  padding: clamp(10px, 1.5vw, 12px) clamp(16px, 2vw, 20px);
  font-size: clamp(14px, 1.8vw, 16px);
  font-weight: bold;
  cursor: pointer;
  min-height: 44px; /* Touch target */
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #0A3D5A;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  img {
    width: clamp(16px, 2vw, 18px);
    height: clamp(16px, 2vw, 18px);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: clamp(250px, 40vw, 300px);
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: clamp(8px, 1.2vw, 10px) clamp(12px, 1.5vw, 16px) clamp(8px, 1.2vw, 10px) clamp(36px, 4vw, 40px);
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  font-size: clamp(13px, 1.6vw, 14px);
  font-weight: 400;
  height: clamp(36px, 4.5vw, 40px);
  box-sizing: border-box;
  background-color: #FAFAFA;
  min-height: 44px; /* Touch target */
  
  &::placeholder {
    color: #9CA3AF;
    font-size: clamp(13px, 1.6vw, 14px);
  }
  
  &:focus {
    outline: none;
    border-color: #042B41;
    background-color: white;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: clamp(10px, 1.2vw, 12px);
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
  color: #9CA3AF;
  
  svg {
    width: clamp(14px, 1.8vw, 16px);
    height: clamp(14px, 1.8vw, 16px);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
`;

const FilterInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: clamp(12px, 1.6vw, 14px);
  color: #666;
  flex-wrap: wrap;
  gap: 8px;
`;

const FilterTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #f0f5ff;
  color: #042B41;
  border: 1px solid #d0e0ff;
  border-radius: 4px;
  padding: 0.3rem 0.6rem;
  margin-left: 0.5rem;
  font-size: 13px;
`;

const ClearFilterButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 6px;
  padding: 2px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #333;
  }
`;

// Enhanced Table with responsive design
const Table = styled.table`
  width: 100%;
  min-width: clamp(1200px, 120vw, 1600px); /* Responsive minimum width */
  border-collapse: collapse;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e0e0e0;
  
  /* Progressive scaling for different zoom levels */
  @media (min-resolution: 144dpi) {
    min-width: clamp(1000px, 100vw, 1400px);
  }
  
  @media (min-resolution: 192dpi) {
    min-width: clamp(800px, 90vw, 1200px);
  }
`;

const CompactHeader = styled.th`
  padding: clamp(3px, 0.5vw, 4px) clamp(4px, 0.8vw, 6px) !important;
  font-size: clamp(10px, 1.2vw, 11px);
  font-weight: 500;
  text-align: center !important;
  width: auto !important;
  white-space: nowrap;
  height: clamp(24px, 3vw, 28px);
  vertical-align: middle;
  border-right: 1px solid #0A3D5A;
  border-left: 1px solid #0A3D5A;
  
  &:last-child {
    border-right: none;
  }
  
  /* Enhanced readability on small screens */
  @media (max-width: 768px) {
    font-size: clamp(9px, 1.1vw, 10px);
    padding: 2px 4px !important;
  }
`;

const TableHeader = styled.thead`
  background-color: #042B41;
  color: white;
  font-family: 'Avenir', sans-serif;
  
  th {
    padding: clamp(4px, 0.8vw, 6px) clamp(6px, 1vw, 8px);
    text-align: left;
    font-weight: 500;
    border-right: 1px solid #0A3D5A;
    border-left: 1px solid #0A3D5A;
    height: clamp(32px, 4vw, 36px);
    vertical-align: middle;
    font-size: clamp(11px, 1.4vw, 12px);
    white-space: nowrap;
    transition: font-size 0.2s ease;
    
    &:last-child {
      border-right: none;
    }
    
    /* Progressive scaling for different zoom levels */
    @media (min-resolution: 144dpi) {
      font-size: clamp(10px, 1.3vw, 11px);
      padding: clamp(3px, 0.7vw, 5px) clamp(5px, 0.9vw, 7px);
    }
    
    @media (max-width: 768px) {
      font-size: clamp(10px, 1.2vw, 11px);
      padding: 3px 5px;
      height: clamp(28px, 3.5vw, 32px);
    }
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #e0e0e0;
    min-height: clamp(36px, 4.5vw, 40px);
  }

  td {
    padding: clamp(4px, 0.8vw, 6px) clamp(6px, 1vw, 8px);
    color: #333;
    border: 1px solid #e0e0e0;
    text-align: left;
    vertical-align: middle;
    font-size: clamp(11px, 1.4vw, 12px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: font-size 0.2s ease;
    
    &.wrap {
      white-space: normal;
      word-break: break-word;
      overflow: visible;
      text-overflow: unset;
    }
    
    /* Progressive scaling for different zoom levels */
    @media (min-resolution: 144dpi) {
      font-size: clamp(10px, 1.3vw, 11px);
      padding: clamp(3px, 0.7vw, 5px) clamp(5px, 0.9vw, 7px);
    }
    
    @media (max-width: 768px) {
      font-size: clamp(10px, 1.2vw, 11px);
      padding: 3px 5px;
    }
  }
`;

const NotesIcon = styled.img<{ hasNotes?: boolean }>`
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin: 0 auto;
  display: block;
  padding: 2px;
  filter: ${props => props.hasNotes ? 'invert(13%) sepia(95%) saturate(5984%) hue-rotate(358deg) brightness(96%) contrast(115%)' : 'none'};
  
  &:hover {
    opacity: 0.8;
  }
`;

// Action Button styled component (icon-based like customer page)
const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 600px;
  max-width: 90%;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  margin: 0 0 20px 0;
  text-align: center;
  color: #333;
  font-weight: 600;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  margin-bottom: 24px;
  font-family: inherit;
  font-size: 16px;
  resize: none;
`;

// Form styling for edit modal
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #042B41;
    box-shadow: 0 0 0 2px rgba(4, 43, 65, 0.1);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    color: #666;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const Button = styled.button`
  padding: 10px 0;
  width: 180px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.secondary {
    background-color: white;
    color: #333;
    border: 1px solid #d0d0d0;
    
    &:hover {
      background-color: #f5f5f5;
    }
  }
  
  &.primary {
    background-color: #042B41;
    color: white;
    border: 1px solid #042B41;
    
    &:hover {
      background-color: #03223A;
    }
    
    &:disabled {
      background-color: #ccc;
      border-color: #ccc;
      cursor: not-allowed;
    }
  }
`;

const BulkUploadButton = styled.button`
  display: flex;
  align-items: center;
  gap: clamp(6px, 1vw, 8px);
  background-color: #042B41;
  color: white;
  border: none;
  border-radius: 8px;
  padding: clamp(6px, 1vw, 8px) clamp(12px, 2vw, 16px);
  font-size: clamp(13px, 1.6vw, 14px);
  font-weight: 600;
  cursor: pointer;
  height: clamp(32px, 4vw, 36px);
  min-height: 44px; /* Touch target */
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #0A3D5A;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  img {
    width: clamp(14px, 1.8vw, 16px);
    height: clamp(14px, 1.8vw, 16px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    margin-bottom: 8px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: clamp(8px, 1.2vw, 10px);
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }
`;

const SearchActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

// Remove skeleton components and replace with consistent loading
const LoadingRow = styled.tr`
  td {
    text-align: center !important;
    padding: 40px 20px !important;
    border: none !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
`;

const PlaceholderImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #999;
  text-align: center;
`;

const ErrorContainer = styled.div`
  color: #e74c3c;
  font-size: 16px;
  text-align: center;
  margin: 20px 0;
`;

interface InventoryPageProps { }

interface InventoryWithImage extends Inventory {
  imageInfo?: SkuImageInfo;
}

const InventoryPage: React.FC<InventoryPageProps & WithErrorHandlingProps> = ({ handleApiCall }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Notes modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Inventory | null>(null);
  const [noteText, setNoteText] = useState('');
  const [originalNoteText, setOriginalNoteText] = useState('');
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Inventory | null>(null);
  const [editFormData, setEditFormData] = useState({
    carton: '',
    units_per_carton: '',
    carton_dimensions: '',
    weight_per_unit: '',
    weight_per_carton: '',
    units_per_pallet: '',
    pallet_weight: '',
    available_stock: ''
  });
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  
  // Bulk upload modal state
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isImageUploadModalOpen, setIsImageUploadModalOpen] = useState(false);

  // Inventory update tracking state
  const [inventoryUpdateCounter, setInventoryUpdateCounter] = useState(0);

  // API call tracking
  const apiCallCounter = useRef(0);
  
  // Track pending updates to handle race conditions
  const pendingUpdates = useRef<Record<string, any>>({});
  
  // Track if we've processed the URL search param to avoid re-processing
  const urlSearchProcessed = useRef(false);

  const fetchInventory = async () => {
    const currentCall = ++apiCallCounter.current;
    setLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };
      
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      const response = await handleApiCall(() => inventoryService.getInventory(params));
      
      // Only update state if this is the most recent call
      if (currentCall === apiCallCounter.current) {
        if (response && response.items) {
          setInventory(response.items);
          setTotalItems(response.total || response.items.length);
        } else {
          setInventory([]);
          setTotalItems(0);
        }
      }
    } catch (err: any) {
      // Only update error state if this is the most recent call
      if (currentCall === apiCallCounter.current) {
        console.error('Error fetching inventory:', err);
        setError(err.message || 'Failed to load inventory data');
        setInventory([]);
        setTotalItems(0);
      }
    } finally {
      // Only update loading state if this is the most recent call
      if (currentCall === apiCallCounter.current) {
        setLoading(false);
      }
    }
  };

  // Read search query from URL params on mount or when location changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    
    if (searchParam && searchParam.trim() !== '' && !urlSearchProcessed.current) {
      const trimmedSearch = searchParam.trim();
      // Pre-fill the search query from URL param
      setSearchQuery(trimmedSearch);
      urlSearchProcessed.current = true;
      // Clear the search param from URL after a short delay to allow the search to trigger
      setTimeout(() => {
        navigate(location.pathname, { replace: true });
      }, 100);
    } else if (!searchParam) {
      // Reset the flag when there's no search param in URL
      urlSearchProcessed.current = false;
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    fetchInventory();
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchInventory();
      }
    }, 500);
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchQuery]);

  // Track inventory data changes for proper re-rendering
  useEffect(() => {
    console.log('Inventory data changed, triggering re-render:', {
      itemCount: inventory.length,
      updateCounter: inventoryUpdateCounter,
      firstItemId: inventory[0]?.id || 'none',
      firstItemStock: inventory[0]?.available_stock ?? 'none',
      allStocks: inventory.slice(0, 3).map(item => ({ 
        id: item.id, 
        sku: item.sku_code, 
        stock: item.available_stock 
      }))
    });
  }, [inventory, inventoryUpdateCounter]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleNoteIconClick = (item: Inventory, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleEditNotes(item);
  };

  const handleSaveNote = async () => {
    if (!selectedItem) return;

    setLoading(true);
    try {
      const updateData: InventoryUpdate = {
        notes: noteText.trim()
      };
      
      await handleApiCall(() => inventoryService.updateInventory(selectedItem.id, updateData));

      // Update local state immediately
      setInventory(prev => prev.map(item => 
        item.id === selectedItem.id 
          ? { ...item, notes: noteText.trim() }
          : item
      ));

      // Increment update counter to force re-render
      setInventoryUpdateCounter(prev => prev + 1);

      setIsModalOpen(false);
      setSelectedItem(null);
      setNoteText('');
      setOriginalNoteText('');
      
      toast.showToast('Note updated successfully', { type: 'success' });
    } catch (err: any) {
      console.error('Error updating note:', err);
      toast.showToast(`Failed to update note: ${err.message}`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setNoteText('');
    setOriginalNoteText('');
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleBack = () => {
    navigate('/home');
  };

  const handleAddInventory = () => {
    // Implementation for adding inventory items
    console.log('Add inventory item');
  };

  const handleBulkUpload = () => {
    setIsBulkUploadModalOpen(true);
  };

  const handleBulkUploadSuccess = () => {
    // Refresh inventory data
    fetchInventory();
    setIsBulkUploadModalOpen(false);
  };

  const handleImageUpload = () => {
    setIsImageUploadModalOpen(true);
  };

  const handleImageUploadSuccess = () => {
    // Refresh inventory data
    fetchInventory();
    setIsImageUploadModalOpen(false);
  };

  // Helper function to parse carton dimensions
  const parseCartonDimensions = useCallback((dimensionsStr: string | null): { length: number | null, breadth: number | null, height: number | null } => {
    if (!dimensionsStr || dimensionsStr.trim() === '') {
      return { length: null, breadth: null, height: null };
    }

    try {
      const matches = dimensionsStr.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/);
      if (matches) {
        return {
          length: parseFloat(matches[1]),
          breadth: parseFloat(matches[2]),
          height: parseFloat(matches[3])
        };
      }
    } catch (error) {
      console.warn('Error parsing dimensions:', error);
    }

    return { length: null, breadth: null, height: null };
  }, []);

  // New edit functionality
  const handleEditClick = useCallback((item: Inventory) => {
    setEditingItem(item);
    setEditFormData({
      carton: item.carton || '',
      units_per_carton: item.units_per_carton?.toString() || '',
      carton_dimensions: item.carton_dimensions || '',
      weight_per_unit: item.weight_per_unit?.toString() || '',
      weight_per_carton: item.weight_per_carton?.toString() || '',
      units_per_pallet: item.units_per_pallet?.toString() || '',
      pallet_weight: item.pallet_weight?.toString() || '',
      available_stock: item.available_stock?.toString() || ''
    });
    setIsEditModalOpen(true);
  }, []);

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    
    setIsUpdateLoading(true);
    try {
      // Parse dimensions from the form data
      const dimensions = parseCartonDimensions(editFormData.carton_dimensions);
      
      // Store the original available_stock value for comparison
      const originalAvailableStock = editingItem.available_stock;
      const newAvailableStock = editFormData.available_stock ? parseFloat(editFormData.available_stock) : undefined;
      
      const updateData: InventoryUpdate = {
        carton: editFormData.carton || undefined,
        units_per_carton: editFormData.units_per_carton ? parseInt(editFormData.units_per_carton) : undefined,
        carton_length: dimensions.length || undefined,
        carton_breadth: dimensions.breadth || undefined,
        carton_height: dimensions.height || undefined,
        weight_per_unit: editFormData.weight_per_unit ? parseFloat(editFormData.weight_per_unit) : undefined,
        weight_per_carton: editFormData.weight_per_carton ? parseFloat(editFormData.weight_per_carton) : undefined,
        units_per_pallet: editFormData.units_per_pallet ? parseInt(editFormData.units_per_pallet) : undefined,
        pallet_weight: editFormData.pallet_weight ? parseFloat(editFormData.pallet_weight) : undefined,
        available_stock: newAvailableStock
      };
      
      console.log('Updating inventory item:', {
        itemId: editingItem.id,
        skuCode: editingItem.sku_code,
        oldStock: originalAvailableStock,
        newStock: newAvailableStock,
        updateData
      });
      
      // Create the optimistically updated item for immediate UI update
      const optimisticItem: Inventory = {
        ...editingItem,
        carton: updateData.carton !== undefined ? updateData.carton : editingItem.carton,
        units_per_carton: updateData.units_per_carton !== undefined ? updateData.units_per_carton : editingItem.units_per_carton,
        carton_dimensions: editFormData.carton_dimensions || editingItem.carton_dimensions,
        carton_length: updateData.carton_length !== undefined ? updateData.carton_length : editingItem.carton_length,
        carton_breadth: updateData.carton_breadth !== undefined ? updateData.carton_breadth : editingItem.carton_breadth,
        carton_height: updateData.carton_height !== undefined ? updateData.carton_height : editingItem.carton_height,
        weight_per_unit: updateData.weight_per_unit !== undefined ? updateData.weight_per_unit : editingItem.weight_per_unit,
        weight_per_carton: updateData.weight_per_carton !== undefined ? updateData.weight_per_carton : editingItem.weight_per_carton,
        units_per_pallet: updateData.units_per_pallet !== undefined ? updateData.units_per_pallet : editingItem.units_per_pallet,
        pallet_weight: updateData.pallet_weight !== undefined ? updateData.pallet_weight : editingItem.pallet_weight,
        available_stock: newAvailableStock !== undefined ? newAvailableStock : editingItem.available_stock
      };
      
      // Update local state immediately for instant UI feedback
      console.log('Updating local state immediately with optimistic update:', {
        itemId: optimisticItem.id,
        newStock: optimisticItem.available_stock,
        fullItem: optimisticItem
      });
      
      // Store the optimistic item in a ref to use later for comparison
      const optimisticItemRef = { ...optimisticItem };
      
      setInventory(prev => prev.map(item => 
        item.id === editingItem.id ? optimisticItem : item
      ));
      
      // Increment update counter to force immediate re-render
      setInventoryUpdateCounter(prev => {
        const newCounter = prev + 1;
        console.log('Incrementing update counter for immediate re-render:', newCounter);
        return newCounter;
      });
      
      // Close modal immediately for better UX
      setIsEditModalOpen(false);
      setEditingItem(null);
      setEditFormData({
        carton: '',
        units_per_carton: '',
        carton_dimensions: '',
        weight_per_unit: '',
        weight_per_carton: '',
        units_per_pallet: '',
        pallet_weight: '',
        available_stock: ''
      });
      
      // Now make the API call in the background
      const updatedItem = await handleApiCall(() => inventoryService.updateInventory(editingItem.id, updateData));
      
      // If API call succeeds, check if the response is what we expected
      if (updatedItem) {
        console.log('API update successful, checking server response:', {
          updatedItemId: updatedItem.id,
          serverStock: updatedItem.available_stock,
          optimisticStock: optimisticItemRef.available_stock
        });
        
        // If the server returned 0 but we expected a different value, keep our optimistic update
        if (updatedItem.available_stock === 0 && newAvailableStock !== 0) {
          console.warn('Server returned 0 for available_stock when we expected:', newAvailableStock);
          console.log('Keeping optimistic update value in UI');
          
          // Keep our optimistic update but update other fields from server
          const correctedItem = {
            ...updatedItem,
            available_stock: optimisticItemRef.available_stock
          };
          
          setInventory(prev => prev.map(item => 
            item.id === editingItem.id ? correctedItem : item
          ));
          
          // Force another re-render
          setInventoryUpdateCounter(prev => prev + 1);
          
          // Attempt a second update after a delay to ensure the correct value is saved
          setTimeout(async () => {
            try {
              console.log('Attempting follow-up update to correct available_stock:', newAvailableStock);
              await handleApiCall(() => inventoryService.updateInventory(editingItem.id, { available_stock: newAvailableStock }));
            } catch (retryErr) {
              console.error('Failed to correct available_stock in follow-up request:', retryErr);
            }
          }, 1000);
        } else {
          // Normal case - server returned expected value or a legitimate different value
          setInventory(prev => prev.map(item => 
            item.id === editingItem.id ? updatedItem : item
          ));
          
          // Force a re-render
          setInventoryUpdateCounter(prev => prev + 1);
        }
      }
      
      toast.showToast('Inventory item updated successfully', { type: 'success' });
    } catch (err: any) {
      console.error('Error updating inventory:', err);
      
      // Revert optimistic update on error
      setInventory(prev => prev.map(item => 
        item.id === editingItem.id ? editingItem : item
      ));
      setInventoryUpdateCounter(prev => prev + 1);
      
      toast.showToast(`Failed to update inventory: ${err.message}`, { type: 'error' });
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingItem(null);
    setEditFormData({
      carton: '',
      units_per_carton: '',
      carton_dimensions: '',
      weight_per_unit: '',
      weight_per_carton: '',
      units_per_pallet: '',
      pallet_weight: '',
      available_stock: ''
    });
  };

  const handleEditNotes = useCallback((item: Inventory) => {
    setSelectedItem(item);
    setNoteText(item.notes || '');
    setOriginalNoteText(item.notes || '');
    setIsModalOpen(true);
  }, []);

  // Remove the renderSkeletonRows function and replace with consistent loading
  const renderTableContent = useMemo(() => {
    console.log('renderTableContent re-rendering due to dependency change:', {
      isLoading: loading,
      inventoryCount: inventory.length,
      updateCounter: inventoryUpdateCounter,
      firstItemStock: inventory[0]?.available_stock || 'none'
    });
    
    if (loading) {
      return (
        <LoadingRow>
          <td colSpan={17}>
            <LoadingContainer>
              <LoadingSpinner size="lg" />
              <LoadingText>Loading inventory</LoadingText>
            </LoadingContainer>
          </td>
        </LoadingRow>
      );
    }

    if (inventory.length === 0) {
      return (
        <tr>
          <td colSpan={17} style={{ textAlign: 'center', padding: '20px' }}>
            No inventory items found.
          </td>
        </tr>
      );
    }

    return inventory.map(item => {
      // Parse dimensions for display
      const dimensions = parseCartonDimensions(item.carton_dimensions);
      return (
        <tr key={item.id}>
          <td className="wrap" style={{ width: '90px', maxWidth: '90px' }}>{item.sku_code}</td>
          <td style={{ width: '50px', textAlign: 'center' }}>
            <SkuImage
              imagePath={item.image_path}
              alt="SKU Image"
              width={35}
              height={35}
              enableModal={true}
              showBlankWhenNoImage={true}
            />
          </td>
          <td className="wrap" style={{ width: '100px', maxWidth: '100px' }}>{item.style_code}</td>
          <td className="wrap" style={{ width: '180px', maxWidth: '180px' }}>{item.description || '-'}</td>
          <td className="wrap" style={{ width: '120px', maxWidth: '120px' }}>{item.supplier_name}</td>
          <td style={{ textAlign: 'right', width: '50px' }}>{item.carton === null || item.carton === undefined || item.carton === '' ? '-' : item.carton}</td>
          <td style={{ textAlign: 'right', width: '80px' }}>{item.units_per_carton === null || item.units_per_carton === undefined ? '-' : item.units_per_carton}</td>
          <td style={{ textAlign: 'right', width: '60px' }}>{dimensions.length === null ? '-' : dimensions.length}</td>
          <td style={{ textAlign: 'right', width: '60px' }}>{dimensions.breadth === null ? '-' : dimensions.breadth}</td>
          <td style={{ textAlign: 'right', width: '60px' }}>{dimensions.height === null ? '-' : dimensions.height}</td>
          <td style={{ textAlign: 'right', width: '80px' }}>{item.weight_per_unit === null || item.weight_per_unit === undefined ? '-' : item.weight_per_unit}</td>
          <td style={{ textAlign: 'right', width: '80px' }}>{item.weight_per_carton === null || item.weight_per_carton === undefined ? '-' : item.weight_per_carton}</td>
          <td style={{ textAlign: 'right', width: '100px' }}>{item.units_per_pallet === null || item.units_per_pallet === undefined ? '-' : item.units_per_pallet}</td>
          <td style={{ textAlign: 'right', width: '80px' }}>{item.pallet_weight === null || item.pallet_weight === undefined ? '-' : item.pallet_weight}</td>
          <td style={{ 
            textAlign: 'right', 
            width: '70px',
            fontWeight: item.available_stock !== null && item.available_stock !== undefined ? 'bold' : 'normal',
            color: item.available_stock !== null && item.available_stock !== undefined ? '#042B41' : '#666'
          }}>
            {item.available_stock !== null && item.available_stock !== undefined ? item.available_stock : '-'}
          </td>
          <td style={{ width: '50px' }}>
            <ActionButtonsContainer>
              <NotesIcon
                src={notesIcon}
                alt="Add/Edit Note"
                hasNotes={!!item.notes && item.notes.length > 0}
                onClick={(e) => handleEditNotes(item)}
              />
            </ActionButtonsContainer>
          </td>
          <td style={{ width: '70px', textAlign: 'center' }}>
            <ActionsCell>
              <ActionButton onClick={() => handleEditClick(item)} title="Edit">
                <img src={customerEditIcon} alt="Edit" />
              </ActionButton>
            </ActionsCell>
          </td>
        </tr>
      );
    });
  }, [loading, inventory, inventoryUpdateCounter, parseCartonDimensions, handleEditNotes, handleEditClick]);

  return (
    <Layout>
      <PageWrapper>
        <ContentContainer>
          <TopSection>
            <TitleSection>
              <BackButtonComponent onClick={handleBack} compact={true} />
              <PageTitle>Inventory</PageTitle>
            </TitleSection>
          </TopSection>

          <SearchActionsContainer>
            <SearchContainer>
              <SearchIconWrapper>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder="Search inventory"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
            <ButtonsContainer>
              <BulkUploadButton onClick={handleImageUpload}>
                <img src={imageUploadIcon} alt="Upload" />
                Upload Images
              </BulkUploadButton>
              <BulkUploadButton onClick={handleBulkUpload}>
                <img src={supplierBulkUploadIcon} alt="Upload" />
                Bulk Upload
              </BulkUploadButton>
            </ButtonsContainer>
          </SearchActionsContainer>

          {error ? (
            <ErrorContainer>
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '18px', color: '#e74c3c', marginBottom: '10px' }}>
                  ⚠️ {error}
                </div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
                  This might be a temporary issue. Please try refreshing the data.
                </div>
                <button
                  onClick={() => {
                    setError(null);
                    fetchInventory();
                  }}
                  style={{
                    backgroundColor: '#3498db',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  🔄 Retry
                </button>
              </div>
            </ErrorContainer>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHeader>
                    <tr>
                      <th style={{ width: '90px' }}>SKU Code</th>
                      <th style={{ width: '50px', textAlign: 'center' }}>SKU Image</th>
                      <th style={{ width: '100px' }}>Style Code</th>
                      <th style={{ width: '180px' }}>Product Description</th>
                      <th style={{ width: '120px' }}>Supplier Name</th>
                      <th style={{ textAlign: 'right', width: '50px' }}>Carton</th>
                      <th style={{ textAlign: 'right', width: '80px' }}>Units Per Carton</th>
                      <th colSpan={3} style={{ textAlign: 'center' }}>Carton Dimensions</th>
                      <th style={{ textAlign: 'right', width: '80px' }}>Weight Per Unit</th>
                      <th style={{ textAlign: 'right', width: '80px' }}>Weight Per Carton</th>
                      <th style={{ textAlign: 'right', width: '100px' }}>No. of Units Per Pallet</th>
                      <th style={{ textAlign: 'right', width: '80px' }}>Pallet Weight</th>
                      <th style={{ textAlign: 'right', width: '70px' }}>Available Stock</th>
                      <th style={{ width: '50px' }}>Notes</th>
                      <th style={{ width: '70px', textAlign: 'center' }}>Actions</th>
                    </tr>
                    <tr>
                      <th colSpan={7}></th>
                      <CompactHeader style={{ width: '60px' }}>Length</CompactHeader>
                      <CompactHeader style={{ width: '60px' }}>Breadth</CompactHeader>
                      <CompactHeader style={{ width: '60px' }}>Height</CompactHeader>
                      <th colSpan={7}></th>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {renderTableContent}
                  </TableBody>
                </Table>
              </TableContainer>

              {!loading && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(totalItems / itemsPerPage)}
                  onPageChange={handlePageChange}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemsPerPageOptions={[10, 20, 50]}
                  showItemsPerPage={true}
                />
              )}
            </>
          )}

          {isModalOpen && (
            <Modal onClick={handleCloseModal}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalTitle>
                  {selectedItem ? `Notes for ${selectedItem.sku_code}` : 'Notes'}
                </ModalTitle>
                <TextArea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Enter your note here"
                  autoFocus
                />
                <ButtonGroup>
                  <Button className="secondary" onClick={handleCloseModal}>Close</Button>
                  <Button
                    className="primary"
                    onClick={handleSaveNote}
                    disabled={loading || noteText === originalNoteText}
                  >
                    {loading ? 'Saving' : 'Save'}
                  </Button>
                </ButtonGroup>
              </ModalContent>
            </Modal>
          )}

          {/* Edit Modal */}
          {isEditModalOpen && editingItem && (
            <Modal onClick={handleCloseEditModal}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <ModalTitle>
                  Edit Inventory Details - {editingItem.sku_code}
                </ModalTitle>
                <FormGrid>
                  <FormGroup>
                    <FormLabel>Carton</FormLabel>
                    <FormInput
                      type="text"
                      value={editFormData.carton}
                      onChange={(e) => handleEditFormChange('carton', e.target.value)}
                      placeholder="Enter carton number"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Units Per Carton</FormLabel>
                    <FormInput
                      type="number"
                      min="0"
                      step="1"
                      value={editFormData.units_per_carton}
                      onChange={(e) => handleEditFormChange('units_per_carton', e.target.value)}
                      placeholder="Enter units per carton"
                    />
                  </FormGroup>
                  <FormGroup className="full-width">
                    <FormLabel>Carton Dimensions</FormLabel>
                    <FormInput
                      type="text"
                      value={editFormData.carton_dimensions}
                      onChange={(e) => handleEditFormChange('carton_dimensions', e.target.value)}
                      placeholder="e.g., 40x30x25 cm"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Weight Per Unit (kg)</FormLabel>
                    <FormInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={editFormData.weight_per_unit}
                      onChange={(e) => handleEditFormChange('weight_per_unit', e.target.value)}
                      placeholder="Enter weight per unit"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Weight Per Carton (kg)</FormLabel>
                    <FormInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={editFormData.weight_per_carton}
                      onChange={(e) => handleEditFormChange('weight_per_carton', e.target.value)}
                      placeholder="Enter weight per carton"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>No. of Units Per Pallet</FormLabel>
                    <FormInput
                      type="number"
                      min="0"
                      step="1"
                      value={editFormData.units_per_pallet}
                      onChange={(e) => handleEditFormChange('units_per_pallet', e.target.value)}
                      placeholder="Enter units per pallet"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Pallet Weight (kg)</FormLabel>
                    <FormInput
                      type="number"
                      min="0"
                      step="0.01"
                      value={editFormData.pallet_weight}
                      onChange={(e) => handleEditFormChange('pallet_weight', e.target.value)}
                      placeholder="Enter pallet weight"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Available Stock</FormLabel>
                    <FormInput
                      type="number"
                      min="0"
                      step="1"
                      value={editFormData.available_stock}
                      onChange={(e) => handleEditFormChange('available_stock', e.target.value)}
                      placeholder="Enter available stock quantity"
                    />
                  </FormGroup>
                </FormGrid>
                <ButtonGroup>
                  <Button className="secondary" onClick={handleCloseEditModal}>Cancel</Button>
                  <Button
                    className="primary"
                    onClick={handleSaveEdit}
                    disabled={isUpdateLoading}
                  >
                    {isUpdateLoading ? 'Updating...' : 'Update'}
                  </Button>
                </ButtonGroup>
              </ModalContent>
            </Modal>
          )}
        </ContentContainer>
      </PageWrapper>

      {/* Bulk Upload Modal */}
      {isBulkUploadModalOpen && (
        <BulkUploadModal
          onClose={() => setIsBulkUploadModalOpen(false)}
          onSuccess={handleBulkUploadSuccess}
        />
      )}

      {/* Image Upload Modal */}
      {isImageUploadModalOpen && (
        <ImageUploadModal
          onClose={() => setIsImageUploadModalOpen(false)}
          onSuccess={handleImageUploadSuccess}
        />
      )}

      {/* Toast notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => { }}
        />
      )}
    </Layout>
  );
};

export default withErrorHandling(InventoryPage);